import { KaotoFunction, KaotoFunctionArgument } from '@kaoto/camel-catalog/catalog-index.d.ts';

import { ComponentsCatalog, ComponentsCatalogTypes } from '../../camel/camel-catalog-index';
import { ICamelComponentDefinition } from '../../camel/camel-components-catalog';
import { ICamelDataformatDefinition } from '../../camel/camel-dataformats-catalog';
import { ICamelLanguageDefinition } from '../../camel/camel-languages-catalog';
import { ICamelLoadBalancerDefinition } from '../../camel/camel-loadbalancers-catalog';
import { ICamelProcessorDefinition } from '../../camel/camel-processors-catalog';
import { IKameletDefinition } from '../../camel/kamelets-catalog';
import { CatalogKind } from '../../catalog-kind';
import { ICitrusComponentDefinition } from '../../citrus/citrus-catalog';

export class CamelCatalogService {
  private static catalogs: ComponentsCatalog = {};

  /**
   * Definitions contributed by a plugin, kept apart from the fetched catalog.
   *
   * A plugin can supply components of its own — the host application runs a Camel build that has
   * them on its classpath — and until the catalog knows their shape, clicking one on the canvas
   * builds no form and the whole node reads as "cannot be configured yet".
   *
   * They are held separately rather than merged in because the catalog arrives asynchronously and
   * replaces itself wholesale: anything written into it before that lands would simply disappear.
   */
  private static contributed: ComponentsCatalog = {};

  static setCatalogKey<CATALOG_KEY extends CatalogKind>(
    catalogKey: CATALOG_KEY,
    catalog?: ComponentsCatalog[CATALOG_KEY],
  ): void {
    this.catalogs[catalogKey] = catalog;
  }

  /**
   * Adds definitions a plugin supplies, in any order relative to the catalog being fetched.
   *
   * The fetched catalog still wins for any name it holds, so a plugin cannot quietly redefine a
   * component that Camel itself describes.
   */
  static addContributed<CATALOG_KEY extends CatalogKind>(
    catalogKey: CATALOG_KEY,
    entries: NonNullable<ComponentsCatalog[CATALOG_KEY]>,
  ): void {
    this.contributed[catalogKey] = {
      ...(this.contributed[catalogKey] ?? {}),
      ...entries,
    } as ComponentsCatalog[CATALOG_KEY];
  }

  /** What plugins contributed and the fetched catalog does not already hold, for listing them. */
  static getContributed<CATALOG_KEY extends CatalogKind>(
    catalogKey: CATALOG_KEY,
  ): NonNullable<ComponentsCatalog[CATALOG_KEY]> {
    const fetched = this.catalogs[catalogKey] ?? {};
    return Object.fromEntries(
      Object.entries(this.contributed[catalogKey] ?? {}).filter(([name]) => !(name in fetched)),
    ) as NonNullable<ComponentsCatalog[CATALOG_KEY]>;
  }

  static getComponent(catalogKey: CatalogKind.Component, componentName?: string): ICamelComponentDefinition | undefined;
  static getComponent(catalogKey: CatalogKind.Processor, componentName?: string): ICamelProcessorDefinition | undefined;
  static getComponent(catalogKey: CatalogKind.Pattern, patternName?: string): ICamelProcessorDefinition | undefined;
  static getComponent(catalogKey: CatalogKind.Entity, entityName?: string): ICamelProcessorDefinition | undefined;
  static getComponent(catalogKey: CatalogKind.Language, languageName?: string): ICamelLanguageDefinition | undefined;
  static getComponent(
    catalogKey: CatalogKind.Dataformat,
    dataformatName?: string,
  ): ICamelDataformatDefinition | undefined;
  static getComponent(
    catalogKey: CatalogKind.Loadbalancer,
    loadBalancerName?: string,
  ): ICamelLoadBalancerDefinition | undefined;
  static getComponent(catalogKey: CatalogKind.Kamelet, componentName?: string): IKameletDefinition | undefined;
  static getComponent(
    catalogKey: CatalogKind.TestActionGroup,
    componentName?: string,
  ): ICitrusComponentDefinition | undefined;
  static getComponent(
    catalogKey: CatalogKind.TestAction,
    componentName?: string,
  ): ICitrusComponentDefinition | undefined;
  static getComponent(
    catalogKey: CatalogKind.TestContainer,
    componentName?: string,
  ): ICitrusComponentDefinition | undefined;
  static getComponent(
    catalogKey: CatalogKind.TestEndpoint,
    componentName?: string,
  ): ICitrusComponentDefinition | undefined;
  static getComponent(
    catalogKey: CatalogKind.TestFunction,
    componentName?: string,
  ): ICitrusComponentDefinition | undefined;
  static getComponent(
    catalogKey: CatalogKind.TestValidationMatcher,
    componentName?: string,
  ): ICitrusComponentDefinition | undefined;
  static getComponent(
    catalogKey: CatalogKind.Function,
    componentName?: string,
  ): Record<string, KaotoFunction<KaotoFunctionArgument>> | undefined;
  static getComponent(catalogKey: CatalogKind, componentName?: string): ComponentsCatalogTypes | undefined;
  static getComponent(catalogKey: CatalogKind, componentName?: string): ComponentsCatalogTypes | undefined {
    if (componentName === undefined) return undefined;

    return this.catalogs[catalogKey]?.[componentName] ?? this.contributed[catalogKey]?.[componentName];
  }

  /**
   * Public only as a convenience method for test
   * not meant to be used in production code
   */
  static clearCatalogs(): void {
    this.catalogs = {};
    this.contributed = {};
  }
}
